import { Button, Icon, Modal, Spinner, Typography } from "@/components/atoms";
import { BookingDiffRow } from "@/redux/slices/clientBooking/bookingDiff";

interface BookingUpdateReviewModalProps {
  open: boolean;
  /** Only the records that changed (From → To) — built by buildBookingDiff. */
  changes: BookingDiffRow[];
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * "Save Changes to This Booking?" — the confirmation gate before an edit is committed. Lists only the
 * records that were updated (From → To); "Keep Editing" dismisses, "Confirm Booking Updates?" runs the
 * PUT. Everyone on the booking may be notified, so the user reviews the exact changes first.
 */
export const BookingUpdateReviewModal = ({ open, changes, onClose, onConfirm, loading = false }: BookingUpdateReviewModalProps) => (
  <Modal
    open={open}
    onClose={loading ? () => undefined : onClose}
    variant="web-large"
    title="Save Changes to This Booking?"
    description="Review the changes below before saving. Everyone involved in this booking may be notified of the updates."
    renderButtons={() => (
      // Full-width stacked on mobile; natural-width pushed to the right on sm+.
      <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
        <Button variant="tertiary" size="lg" onClick={onClose} disabled={loading} className="w-full sm:w-auto">
          Keep Editing
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={onConfirm}
          disabled={loading || changes.length === 0}
          className="w-full sm:w-auto"
          startIcon={loading ? <Spinner variant="light" /> : undefined}
        >
          Confirm Booking Updates?
        </Button>
      </div>
    )}
  >
    <div className="overflow-hidden rounded-lg border border-neutral-mid-gray">
      {/* Header row */}
      <div className="grid grid-cols-[1fr_1fr_auto_1fr] items-center gap-2 border-b border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2">
        <Typography variant="small">Record</Typography>
        <Typography variant="small">From</Typography>
        <span aria-hidden />
        <Typography variant="small">To</Typography>
      </div>
      {changes.map((row) => (
        <div
          key={row.key}
          className="grid grid-cols-[1fr_1fr_auto_1fr] items-center gap-2 border-b border-neutral-mid-gray px-3 py-2.5 last:border-b-0"
        >
          <Typography variant="small">{row.label}:</Typography>
          <Typography variant="small-regular" className="text-neutral-dark-gray">
            {row.from}
          </Typography>
          <Icon name="ArrowRight" size="sm" className="text-neutral-dark-gray" />
          <Typography variant="small-regular">{row.to}</Typography>
        </div>
      ))}
    </div>
  </Modal>
);
